'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import LearnerTopbar from '@/components/LearnerTopbar';
import SiteFooter from '@/components/SiteFooter';
import CertificateModal from '@/components/CertificateModal';
import { createClient } from '@/lib/supabase/client';
import {
  BookOpen,
  CheckCircle2,
  Clock,
  Trophy,
  TrendingUp,
  BarChart2,
  ChevronRight,
  Star,
  Target,
  Award,
} from 'lucide-react';

interface UserProfile {
  full_name: string;
  student_id: string | null;
  email: string;
  program: string | null;
  created_at: string;
}

interface ConceptMastery {
  subject_id: string;
  topic_id: string;
  topic_title: string;
  mastery_level: string;
  quiz_attempts: number;
  quiz_passes: number;
  last_score: number;
  best_score: number;
}

interface LearningSession {
  subject_id: string;
  topic_id: string;
  quiz_score: number | null;
  duration_seconds: number;
  created_at: string;
}

interface SubjectStats {
  subjectId: string;
  subjectTitle: string;
  totalTopics: number;
  completedTopics: number;
  avgScore: number;
  totalMinutes: number;
  masteryLevels: Record<string, number>;
}

const SUBJECT_TITLES: Record<string, string> = {
  'subj-python': 'Python Programming',
  'subj-stats': 'Statistics',
  'subj-philosophy': 'Philosophy of Mind',
};

const MASTERY_COLORS: Record<string, string> = {
  mastered: 'bg-green-500',
  solid: 'bg-primary',
  developing: 'bg-yellow-500',
  weak: 'bg-red-400',
  not_started: 'bg-muted',
};

const MASTERY_LABELS: Record<string, string> = {
  mastered: 'Mastered',
  solid: 'Solid',
  developing: 'Developing',
  weak: 'Weak',
  not_started: 'Not Started',
};

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
}

function getMasteryBadge(level: string) {
  const color = MASTERY_COLORS[level] ?? 'bg-muted';
  const label = MASTERY_LABELS[level] ?? level;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white ${color}`}>
      {label}
    </span>
  );
}

export default function LearnerProfilePage() {
  const router = useRouter();
  const supabase = createClient();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [masteryData, setMasteryData] = useState<ConceptMastery[]>([]);
  const [sessions, setSessions] = useState<LearningSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [certSubject, setCertSubject] = useState<{ subjectId: string; subjectTitle: string; avgScore: number } | null>(null);

  useEffect(() => {
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        router.push('/login');
        return;
      }
      const userId = session.user.id;

      const [profileRes, masteryRes, sessionsRes] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('full_name, student_id, email, program, created_at')
          .eq('id', userId)
          .single(),
        supabase
          .from('concept_mastery')
          .select('subject_id, topic_id, topic_title, mastery_level, quiz_attempts, quiz_passes, last_score, best_score')
          .eq('user_id', userId),
        supabase
          .from('learning_sessions')
          .select('subject_id, topic_id, quiz_score, duration_seconds, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
      ]);

      if (profileRes.data) setProfile(profileRes.data);
      if (masteryRes.data) setMasteryData(masteryRes.data);
      if (sessionsRes.data) setSessions(sessionsRes.data);
      setLoading(false);
    };
    load();
  }, []);

  // Compute aggregate stats
  const totalTimeSeconds = sessions.reduce((acc, s) => acc + (s.duration_seconds ?? 0), 0);
  const completedTopics = masteryData.filter(
    (m) => m.mastery_level === 'solid' || m.mastery_level === 'mastered'
  ).length;
  const quizScores = masteryData.filter((m) => m.quiz_attempts > 0).map((m) => m.best_score * 100);
  const avgQuizScore = quizScores.length > 0
    ? Math.round(quizScores.reduce((a, b) => a + b, 0) / quizScores.length)
    : 0;

  // Group mastery by subject
  const subjectMap: Record<string, SubjectStats> = {};
  masteryData.forEach((m) => {
    if (!subjectMap[m.subject_id]) {
      subjectMap[m.subject_id] = {
        subjectId: m.subject_id,
        subjectTitle: SUBJECT_TITLES[m.subject_id] ?? m.subject_id,
        totalTopics: 0,
        completedTopics: 0,
        avgScore: 0,
        totalMinutes: 0,
        masteryLevels: {},
      };
    }
    const s = subjectMap[m.subject_id];
    s.totalTopics += 1;
    if (m.mastery_level === 'solid' || m.mastery_level === 'mastered') s.completedTopics += 1;
    s.masteryLevels[m.mastery_level] = (s.masteryLevels[m.mastery_level] ?? 0) + 1;
  });

  // Add time per subject from sessions
  sessions.forEach((s) => {
    if (subjectMap[s.subject_id]) {
      subjectMap[s.subject_id].totalMinutes += Math.round((s.duration_seconds ?? 0) / 60);
    }
  });

  // Compute avg score per subject
  Object.values(subjectMap).forEach((s) => {
    const subjectTopics = masteryData.filter(
      (m) => m.subject_id === s.subjectId && m.quiz_attempts > 0
    );
    if (subjectTopics.length > 0) {
      s.avgScore = Math.round(
        (subjectTopics.reduce((acc, m) => acc + m.best_score, 0) / subjectTopics.length) * 100
      );
    }
  });

  const subjectStats = Object.values(subjectMap);

  // Recent quiz attempts from mastery data
  const recentTopics = [...masteryData]
    .filter((m) => m.quiz_attempts > 0)
    .sort((a, b) => b.best_score - a.best_score)
    .slice(0, 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <LearnerTopbar />
        <main className="pt-16 flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-muted-foreground">Loading your profile…</p>
          </div>
        </main>
      </div>
    );
  }

  // Eligible subjects: completedTopics >= 3 AND avgScore >= 70
  const eligibleSubjects = subjectStats.filter(
    (s) => s.completedTopics >= 3 && s.avgScore >= 70
  );

  const completionDateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-background">
      <LearnerTopbar />
      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Profile Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-10">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-bold text-primary">
                {profile?.full_name?.charAt(0)?.toUpperCase() ?? '?'}
              </span>
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground">{profile?.full_name ?? 'Learner'}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                {profile?.student_id && (
                  <span className="text-sm text-muted-foreground font-mono">{profile.student_id}</span>
                )}
                {profile?.program && (
                  <span className="text-sm text-muted-foreground">{profile.program}</span>
                )}
                {profile?.created_at && (
                  <span className="text-xs text-muted-foreground">
                    Enrolled {new Date(profile.created_at).getFullYear()}
                  </span>
                )}
              </div>
            </div>
            <Link
              href="/"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Continue Learning
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              {
                icon: <BookOpen className="w-5 h-5 text-primary" />,
                label: 'Subjects Started',
                value: subjectStats.length,
                bg: 'bg-primary/8',
              },
              {
                icon: <CheckCircle2 className="w-5 h-5 text-green-500" />,
                label: 'Topics Completed',
                value: completedTopics,
                bg: 'bg-green-500/8',
              },
              {
                icon: <Trophy className="w-5 h-5 text-yellow-500" />,
                label: 'Avg Quiz Score',
                value: quizScores.length > 0 ? `${avgQuizScore}%` : '—',
                bg: 'bg-yellow-500/8',
              },
              {
                icon: <Clock className="w-5 h-5 text-blue-500" />,
                label: 'Time Spent',
                value: totalTimeSeconds > 0 ? formatDuration(totalTimeSeconds) : '—',
                bg: 'bg-blue-500/8',
              },
            ].map((stat) => (
              <div
                key={stat.label}
                className="bg-card border border-border rounded-xl p-4 flex flex-col gap-3"
              >
                <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  {stat.icon}
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Certificates Section */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-4 h-4 text-primary" />
              <h2 className="text-base font-semibold text-foreground">Certificates</h2>
            </div>

            {eligibleSubjects.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground mb-0.5">No certificates yet</p>
                  <p className="text-xs text-muted-foreground">
                    Complete at least <strong>3 topics</strong> in a subject with an average quiz score of <strong>70% or higher</strong> to earn your certificate.
                  </p>
                </div>
                <Link
                  href="/"
                  className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Start Learning
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {eligibleSubjects.map((s) => (
                  <div
                    key={s.subjectId}
                    className="bg-card border border-border rounded-xl p-5 flex flex-col gap-4"
                    style={{ borderTop: '3px solid #F97316' }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Award className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-foreground leading-tight">{s.subjectTitle}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {s.completedTopics} topics · {s.avgScore}% avg score
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                      <span className="text-xs text-green-700 font-medium">Eligible for certificate</span>
                    </div>
                    <button
                      onClick={() => setCertSubject({ subjectId: s.subjectId, subjectTitle: s.subjectTitle, avgScore: s.avgScore })}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <Award className="w-4 h-4" />
                      View Certificate
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Subject Progress — spans 2 cols */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-primary" />
                <h2 className="text-base font-semibold text-foreground">Progress by Subject</h2>
              </div>

              {subjectStats.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-8 text-center">
                  <BookOpen className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No subjects started yet.</p>
                  <Link href="/" className="mt-3 inline-block text-sm text-primary hover:underline">
                    Browse subjects →
                  </Link>
                </div>
              ) : (
                subjectStats.map((s) => {
                  const pct = s.totalTopics > 0
                    ? Math.round((s.completedTopics / s.totalTopics) * 100)
                    : 0;
                  return (
                    <div key={s.subjectId} className="bg-card border border-border rounded-xl p-5">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <h3 className="font-semibold text-foreground text-sm">{s.subjectTitle}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {s.completedTopics}/{s.totalTopics} topics completed
                            {s.totalMinutes > 0 && ` · ${s.totalMinutes}m spent`}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {s.avgScore > 0 && (
                            <span className="text-sm font-bold text-foreground">{s.avgScore}%</span>
                          )}
                          <p className="text-xs text-muted-foreground">avg score</p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-3">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>

                      {/* Mastery breakdown */}
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(s.masteryLevels).map(([level, count]) => (
                          <span
                            key={level}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground"
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${MASTERY_COLORS[level] ?? 'bg-muted-foreground'}`}
                            />
                            {count} {MASTERY_LABELS[level] ?? level}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Right column: Quiz Scores */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart2 className="w-4 h-4 text-primary" />
                <h2 className="text-base font-semibold text-foreground">Quiz Performance</h2>
              </div>

              {recentTopics.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-6 text-center">
                  <Target className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">No quizzes completed yet.</p>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl divide-y divide-border">
                  {recentTopics.map((m) => {
                    const pct = Math.round(m.best_score * 100);
                    return (
                      <div key={m.topic_id} className="px-4 py-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{m.topic_title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {m.quiz_attempts} attempt{m.quiz_attempts !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span
                            className={`text-sm font-bold ${
                              pct >= 80
                                ? 'text-green-500'
                                : pct >= 60
                                ? 'text-yellow-500' :'text-red-400'
                            }`}
                          >
                            {pct}%
                          </span>
                          {getMasteryBadge(m.mastery_level)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Mastery legend */}
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <Star className="w-3.5 h-3.5 text-primary" />
                  <p className="text-xs font-semibold text-foreground">Mastery Levels</p>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(MASTERY_LABELS).map(([level, label]) => (
                    <div key={level} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${MASTERY_COLORS[level]}`} />
                      <span className="text-xs text-muted-foreground">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />

      {/* Certificate Modal */}
      {certSubject && (
        <CertificateModal
          isOpen={!!certSubject}
          onClose={() => setCertSubject(null)}
          learnerName={profile?.full_name ?? 'Learner'}
          subjectTitle={certSubject.subjectTitle}
          completionDate={completionDateStr}
          avgScore={certSubject.avgScore}
          studentId={profile?.student_id}
        />
      )}
    </div>
  );
}
